// @ts-nocheck
import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = 'Start typing...',
  className = '',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-luna-secondary hover:underline',
        },
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `focus:outline-none min-h-[200px] p-4 text-luna-text-light dark:text-luna-text-dark ${className}`,
      },
    },
  });

  if (!editor) {
    return null;
  }

  return (
    <div className="border-2 border-luna-secondary/30 rounded-lg bg-white dark:bg-luna-primary">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-2 border-b border-luna-secondary/30 bg-luna-secondary/5">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('bold') ? 'bg-luna-secondary/20' : ''
          }`}
          title="Bold"
        >
          <span className="material-symbols-outlined text-lg">format_bold</span>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('italic') ? 'bg-luna-secondary/20' : ''
          }`}
          title="Italic"
        >
          <span className="material-symbols-outlined text-lg">format_italic</span>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('strike') ? 'bg-luna-secondary/20' : ''
          }`}
          title="Strikethrough"
        >
          <span className="material-symbols-outlined text-lg">strikethrough_s</span>
        </button>
        <div className="w-px h-6 bg-luna-secondary/30" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('heading', { level: 1 }) ? 'bg-luna-secondary/20' : ''
          }`}
          title="Heading 1"
        >
          <span className="material-symbols-outlined text-lg">title</span>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('heading', { level: 2 }) ? 'bg-luna-secondary/20' : ''
          }`}
          title="Heading 2"
        >
          <span className="material-symbols-outlined text-lg">text_fields</span>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('heading', { level: 3 }) ? 'bg-luna-secondary/20' : ''
          }`}
          title="Heading 3"
        >
          <span className="material-symbols-outlined text-lg">text_fields</span>
        </button>
        <div className="w-px h-6 bg-luna-secondary/30" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('bulletList') ? 'bg-luna-secondary/20' : ''
          }`}
          title="Bullet List"
        >
          <span className="material-symbols-outlined text-lg">format_list_bulleted</span>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('orderedList') ? 'bg-luna-secondary/20' : ''
          }`}
          title="Numbered List"
        >
          <span className="material-symbols-outlined text-lg">format_list_numbered</span>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('blockquote') ? 'bg-luna-secondary/20' : ''
          }`}
          title="Quote"
        >
          <span className="material-symbols-outlined text-lg">format_quote</span>
        </button>
        <div className="w-px h-6 bg-luna-secondary/30" />
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('Enter URL:');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={`p-2 rounded hover:bg-luna-secondary/10 ${
            editor.isActive('link') ? 'bg-luna-secondary/20' : ''
          }`}
          title="Add Link"
        >
          <span className="material-symbols-outlined text-lg">link</span>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link')}
          className="p-2 rounded hover:bg-luna-secondary/10 disabled:opacity-50"
          title="Remove Link"
        >
          <span className="material-symbols-outlined text-lg">link_off</span>
        </button>
        <div className="w-px h-6 bg-luna-secondary/30" />
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          className="p-2 rounded hover:bg-luna-secondary/10 disabled:opacity-50"
          title="Undo"
        >
          <span className="material-symbols-outlined text-lg">undo</span>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          className="p-2 rounded hover:bg-luna-secondary/10 disabled:opacity-50"
          title="Redo"
        >
          <span className="material-symbols-outlined text-lg">redo</span>
        </button>
      </div>

      {/* Editor Content */}
      <div className="min-h-[200px] p-4">
        <EditorContent editor={editor} />
        <style>{`
          .ProseMirror {
            outline: none;
          }
          .ProseMirror p {
            margin: 0.5em 0;
          }
          .ProseMirror h1 {
            font-size: 1.875rem;
            font-weight: 700;
            margin: 1em 0 0.5em 0;
          }
          .ProseMirror h2 {
            font-size: 1.5rem;
            font-weight: 600;
            margin: 0.8em 0 0.4em 0;
          }
          .ProseMirror h3 {
            font-size: 1.25rem;
            font-weight: 600;
            margin: 0.6em 0 0.3em 0;
          }
          .ProseMirror ul, .ProseMirror ol {
            padding-left: 1.5em;
            margin: 0.5em 0;
          }
          .ProseMirror blockquote {
            border-left: 4px solid #8884d8;
            padding-left: 1em;
            margin: 1em 0;
            font-style: italic;
          }
          .ProseMirror a {
            color: #8884d8;
            text-decoration: underline;
          }
        `}</style>
      </div>
    </div>
  );
};

